// ============================================================================
// REFUND & CANCELLATION POLICY — public page (/refund)
// Razorpay checks this page specifically for subscription businesses.
// The numbers come from companyInfo.refund — set them to your real terms.
// ============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout from './LegalLayout';
import companyInfo from './companyInfo';

const { windowDays, processingDays } = companyInfo.refund;

const Refund = () => (
  <LegalLayout
    title="Refund and Cancellation Policy"
    subtitle={`How cancellations and refunds work for ${companyInfo.brandName} subscriptions.`}
  >
    <section>
      <h2>1. Summary</h2>
      <p>
        {companyInfo.brandName} is sold as a prepaid subscription billed per user seat.
        Renewal is manual — nothing is auto-debited, so you cancel simply by choosing not to
        renew.
      </p>
    </section>

    <section>
      <h2>2. Cancellation</h2>
      <p>
        You can stop using the service at any time. Because we do not store your payment
        instrument and do not auto-renew, no cancellation request is needed to prevent a
        future charge — if you do not complete a payment before the cycle ends, the
        subscription simply lapses.
      </p>
      <p>
        Cancelling mid-cycle does not shorten the cycle you have already paid for. Your seats
        remain active until the cycle end date shown in your account.
      </p>
      <p>
        To remove specific users from the next cycle, deselect them when renewing. You are
        only charged for the seats you select.
      </p>
    </section>

    <section>
      <h2>3. Refund eligibility</h2>
      {windowDays > 0 ? (
        <>
          <p>
            A first-time subscription purchase may be refunded if you request it within{' '}
            <strong>{windowDays} days</strong> of the payment date and the platform has not
            been used substantially during that period.
          </p>
          <p>Refunds are generally <strong>not</strong> available for:</p>
        </>
      ) : (
        <p>
          Subscription payments are non-refundable once the billing cycle has started. We
          may still issue a refund at our discretion in the situations listed in section 4.
        </p>
      )}
      <ul>
        <li>renewal payments for a cycle that has already begun;</li>
        <li>the unused portion of a cycle after cancellation;</li>
        <li>seats that were purchased but never activated by the customer;</li>
        <li>prorated mid-cycle seat additions after the seat has been used;</li>
        <li>accounts suspended for breach of our <Link to="/terms">Terms and Conditions</Link>.</li>
      </ul>
      <p className="legal__placeholder">
        [ADJUST THESE EXCLUSIONS TO MATCH THE TERMS YOU ACTUALLY WANT TO OFFER]
      </p>
    </section>

    <section>
      <h2>4. Refunds we always honour</h2>
      <p>Regardless of the above, we will refund you in full where:</p>
      <ul>
        <li>you were charged twice for the same billing cycle;</li>
        <li>an amount was debited but the subscription was never activated and cannot be activated;</li>
        <li>you were charged an amount different from the quoted price;</li>
        <li>a payment was made in error and reported before access was used.</li>
      </ul>
      <p>
        Where a payment succeeds at the gateway but access is not applied due to a technical
        failure on our side, our system reconciles the payment automatically and grants
        access. If access is still not granted, contact us and we will either fix it or
        refund the payment.
      </p>
    </section>

    <section>
      <h2>5. How to request a refund</h2>
      <p>Email {companyInfo.supportEmail} with:</p>
      <ul>
        <li>your registered organisation name and client code;</li>
        <li>the payment reference or order ID;</li>
        <li>the date and amount of the payment;</li>
        <li>the reason for the request.</li>
      </ul>
      <p>
        We will acknowledge your request within 2 working days and tell you whether it is
        approved.
      </p>
    </section>

    <section>
      <h2>6. Processing time</h2>
      <p>
        Approved refunds are issued to the original payment method. We initiate the refund
        within {processingDays} working days of approval. Once initiated, the time for the
        amount to appear in your account depends on your bank or card issuer, and is
        typically 5 to 10 working days.
      </p>
      <p>
        Refunds cannot be issued to a different account, card or UPI ID from the one used for
        the original payment.
      </p>
    </section>

    <section>
      <h2>7. Failed payments</h2>
      <p>
        If a payment fails but the amount is debited from your account, it is usually an
        authorisation hold that the bank reverses automatically within 5 to 7 working days.
        No action is needed from us. If it has not reversed after that, contact us with the
        transaction reference and we will raise it with the gateway.
      </p>
    </section>

    <section>
      <h2>8. Price changes</h2>
      <p>
        Prices published on our <Link to="/pricing">Pricing</Link> page may change. A price
        change never affects a cycle you have already paid for — the per-seat rate is locked
        for the duration of that cycle, including for users added part-way through it.
      </p>
    </section>

    <section>
      <h2>9. Contact</h2>
      <p>
        For any question about this policy, write to {companyInfo.supportEmail} or call{' '}
        {companyInfo.supportPhone} during {companyInfo.supportHours}.
      </p>
    </section>
  </LegalLayout>
);

export default Refund;
